import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import CategoriesPage from "../Components/CategoriesPage";
import WelcomePage from "../Components/WelcomePage";
import HraAlertBanner from "../Components/HraAlertBanner";
import RecentBookings from "../Components/RecentBookings";
import WalletWidget from "../Components/WalletWidget";
import UpcomingWidget from "../Components/UpcomingWidget";
import { Helmet } from "react-helmet";
import LoadingState from "../Components/States/LoadingState";
import { Plus } from "lucide-react";

const HomePage = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Elthium Health - Healthcare Solutions & Medical Services</title>
          <meta
            name="description"
            content="Elthium Health - One Platform, Total Wellness. Your trusted healthcare partner for comprehensive medical services and health solutions."
          />
          <meta
            name="keywords"
            content="Elthium Health, healthcare, medical services, health solutions, wellness platform"
          />
          <link rel="canonical" href="https://credenthealth.com" />
        </Helmet>

        <LoadingState branded fullPage message="One Platform, Total Wellness" />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Elthium Health | Healthcare Solutions & Medical Services</title>
        <meta
          name="description"
          content="Elthium Health offers comprehensive healthcare solutions, medical services, and innovative health management systems. One platform for total wellness and patient care."
        />
        <meta
          name="keywords"
          content="Elthium Health, healthcare solutions, medical services, health platform, wellness, patient care, medical technology"
        />
        <meta
          property="og:title"
          content="Elthium Health - Trusted Healthcare Solutions"
        />
        <meta
          property="og:description"
          content="One Platform, Total Wellness - Your complete healthcare solution provider"
        />
        <meta property="og:url" content="https://credenthealth.com" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://credenthealth.com/logo.png"
        />
        <link rel="canonical" href="https://credenthealth.com" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalOrganization",
              "name": "Elthium Health",
              "url": "https://credenthealth.com",
              "logo": "https://credenthealth.com/logo.png",
              "description": "One Platform, Total Wellness - Comprehensive healthcare solutions and medical services",
              "slogan": "One Platform, Total Wellness"
            }
          `}
        </script>
      </Helmet>

      <div className="flex flex-col min-h-screen bg-background">
        <div className="fixed top-0 left-0 right-0 z-50">
          <Header />
        </div>

        <div className="flex flex-col min-h-screen pb-16 lg:pb-0 pt-16">
          <div className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Main column — desktop order unchanged */}
              <div className="lg:col-span-2">
                <section
                  className="mb-4"
                  aria-label="Welcome to Elthium Health"
                >
                  <WelcomePage />
                </section>

                <section className="lg:hidden mb-4" aria-label="Quick actions">
                  <CategoriesPage mobileTop />
                </section>

                <section
                  className="my-4"
                  aria-label="Health Risk Assessment reminder"
                >
                  <HraAlertBanner />
                </section>

                <section
                  className="my-4 hidden lg:block"
                  aria-label="Healthcare Categories"
                >
                  <CategoriesPage />
                </section>

                <section aria-label="my-4 Recent Lab Reports">
                  <RecentBookings />
                </section>
              </div>

              {/* Sidebar — unchanged on large screens */}
              <aside className="space-y-4 sm:space-y-6">
                <WalletWidget />

                {/* Medical Record Button */}
                <button
                  type="button"
                  onClick={() => navigate("/medicalrecord/upload")}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 font-semibold text-white transition hover:opacity-90"
                >
                  <Plus size={20} />
                  Medical Record
                </button>

                <UpcomingWidget />
              </aside>
            </div>
          </div>

          <div className="mt-auto">
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;